import { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { useForm, Controller } from "react-hook-form";
import { TextInput, FormContainer } from "../../shared";
import { CHANGE_PASSWORD } from "../../../utils/graphql";
import useGlobalMutation from "../../../hooks/useGlobalMutation";
import { RegexPattern } from "../../../utils";
import { samlLogout } from "../../../utils/service";
import { deleteCookie } from "../../../utils/cookies";
import { logoutUser } from "../../../reducers/signout";

const UpdatePassword = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const handleGoBack = () => {
        navigate(-1);
    };
    const {
        register,
        formState: { errors },
        handleSubmit,
        reset,
        control,
        watch,
        setError,
        clearErrors,
    } = useForm({
        mode: "onChange",
        defaultValues: {
            password: "",
        },
    });

    const password = watch("password", "");
    const confirmPassword = watch("confirmPassword", "");

    const { submitMutation, loading } = useGlobalMutation(CHANGE_PASSWORD);

    const submitAction = handleSubmit(async (data) => {
        const { oldPassword, password, confirmPassword } = data;

        if (oldPassword !== password) {
            if (password === confirmPassword) {
                try {
                    const variables = {
                        oldPassword,
                        newPassword: password,
                    };
                    await submitMutation(
                        variables,
                        "changePasswordPiap",
                        "Password changed successfully!!!"
                    );
                    reset();

                    await Promise.all([
                        deleteCookie("piap_jwt_token"),
                        deleteCookie("piap_host_url"),
                        samlLogout(),
                    ]);
                } catch (error) {
                    toast.error("Something went wrong");
                } finally {
                    dispatch(logoutUser());
                }
            } else {
                setError("confirmPassword", { message: "Passwords do not match" });
            }
        } else {
            toast.error("Password cannot be the same as the old password");
        }
    });

    useEffect(() => {
        if (confirmPassword.length > 0 && password !== confirmPassword && !errors.password) {
            setError("confirmPassword", { message: "Passwords do not match" });
        } else {
            clearErrors("confirmPassword");
        }
    }, [password]);

    return (
        <div className="row justify-content-center">
            <div className="col-lg-6 col-md-8">
                <FormContainer
                    loading={loading}
                    customClass="p-5 custom-shadow"
                    submitAction={submitAction}
                    submitButtonText="Update Password"
                >
                    <Link to="#" onClick={handleGoBack} className="btn btn-outline-primary mb-4">
                        Back
                    </Link>
                    <h4 className="fw-bold mb-5">Update Password</h4>

                    <TextInput
                        labelText="Old Password"
                        type="password"
                        required
                        name="oldPassword"
                        id="oldPassword"
                        {...register("oldPassword", {
                            required: true,
                        })}
                    />

                    <Controller
                        name="password"
                        control={control}
                        render={({ field: { ref } }) => (
                            <TextInput
                                id="password"
                                labelText="Password"
                                type="password"
                                isInvalid={!!errors.password}
                                required
                                errorMessage={
                                    (errors.password && errors.password.message) ||
                                    "Invalid Password"
                                }
                                ref={ref}
                                {...register("password", {
                                    required: true,
                                    pattern: RegexPattern.password,
                                })}
                            />
                        )}
                    />

                    <small className="form-text text-muted">
                        The password must be at least 8 characters long and contain 1 number, 1
                        upper and lower case and 1 special character.
                    </small>

                    <Controller
                        name="confirmPassword"
                        control={control}
                        render={({ field: { ref } }) => (
                            <TextInput
                                id="confirmPassword"
                                labelText="Confirm Password"
                                type="password"
                                isInvalid={!!errors.confirmPassword}
                                required
                                errorMessage={
                                    (errors.confirmPassword && errors.confirmPassword.message) ||
                                    "Invalid Password"
                                }
                                ref={ref}
                                {...register("confirmPassword", {
                                    required: true,
                                    validate: (value) =>
                                        value === password || "Passwords do not match",
                                    watch: true,
                                })}
                            />
                        )}
                    />
                </FormContainer>
            </div>
        </div>
    );
};

export default UpdatePassword;
